import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Button,
  Heading,
  LoadingOverlay,
  PanIcon,
  Shimmer,
  TextArea,
  Theme,
  ThemeName,
} from '@taopix/taopix-design-system';
import classNames from 'classnames';
import {useTaopix} from '../../Context/TaopixContext';
import {Tile} from '../../BackgroundsList/BackgroundsList';
import CalendarPicturePalette from './CalendarPicturePalette';
import {AssetMetaInterface, ComputedPageItem} from '@taopix/taopix-core/dist/types/types';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';
import {CellEditorData} from './CalendarPalette';
import CalendarColourPalette from './CalendarColourPalette';
import CloseCellEditorDialog from './CloseCellEditorDialog';
import {useSelector} from 'react-redux';
import {CoreEvent, ViewMode} from '@taopix/taopix-core';
import ToolPaletteSection from '../../ToolPalette/ToolPaletteSection';
import {useExperience} from '../../Experience/ExperienceProvider';

export type CellEditorPaletteProps = {
  sectionKey: string;
  assets: AssetMetaInterface[];
  cellEvent?: CellEditorData;
  loaded: boolean;
  item?: ComputedPageItem;
};

const PALETTE_SECTIONS = [
  'pictures',
  'backgrounds',
  'layouts',
  'stickers',
  'mask',
  'box-settings-palette',
  'project-settings',
  'calendar-palette',
];
const VIEW_MODES = [`${ViewMode.Preview}`];

const CellEditorPalette = ({
  sectionKey,
  assets,
  cellEvent,
  item,
  loaded,
}: CellEditorPaletteProps) => {
  const app = useTaopix();
  const ui = useUiState();
  const {t} = useTranslation(['calendars']);
  const {experience} = useExperience();

  const server = app.core.get('server');
  const calendarManager = window.Taopix.Core.get('calendarManager');
  const calendarSettings: any = useSelector<any>(state => state.project?.calendar?.settings);

  const [disableSaveButton, setDisableSaveButton] = useState(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [showColourPalette, setShowColourPalette] = useState<boolean>(false);
  const [textProcessing, setTextProcessing] = useState<boolean>(false);

  const [customEventData, setCustomEventData] = useState<CellEditorData>(
    cellEvent as CellEditorData
  );

  const [initialCalendarCellEvent, setInitialCalendarCellEvent] = useState<CellEditorData>(
    cellEvent as CellEditorData
  );

  const allowCustomEvents =
    calendarSettings.canAddCustomEvents && experience.EDITOR.calendarEvents.allowCustomEvents;

  const canCustomiseDates = calendarSettings.canCustomiseDates;

  // when event is loaded store the inital data to check if there have been any changes
  useEffect(() => {
    if (cellEvent) {
      setInitialCalendarCellEvent(cellEvent);
      setCustomEventData({
        ref: cellEvent.ref,
        date: cellEvent.date,
        eventText: cellEvent.eventText,
        textColour: cellEvent.textColour,
        hiresAssetRef: cellEvent.hiresAssetRef,
        hasEventSetEvent: cellEvent.hasEventSetEvent,
        dateTitle: cellEvent.dateTitle,
      });
    }
    setIsDirty(false);
  }, [cellEvent]);

  useEffect(() => {
    ui.dispatch(UserInterfaceActions.setToolPaletteDirty(isDirty));
  }, [isDirty]);

  const sectionButtonClasses = classNames('py-md');

  const wrapperClasses = classNames(
    'flex-1',
    'flex',
    'flex-col',
    'w-full',
    'space-y-sm',
    'px-md',
    'pb-md',
    'justify-between',
    'max-w-[400rem]',
    'self-center',
    'overflow-y-auto',
    // Allow the panel content to be scrolled from behind the toolbar
    experience.EDITOR.toolPalette.mobileSlideUpPanels === 'msuo' && 'sm:pb-[110rem]'
  );

  const bottomButtonsContainerClasses = classNames(
    'flex',
    'flex-col',
    'w-full',
    'space-y-xs',
    'items-center'
  );

  const previewContainerClasses = classNames('w-full', 'overflow-hidden', 'p-lg');

  const previewWrapperClasses = classNames(
    'flex',
    'items-center',
    'justify-center',
    'w-full',
    'h-full',
    // Add a border to the konva element to differentiate it from the container background
    '[&>div]:outline',
    '[&>div]:outline-1',
    '[&>div]:outline-themeTextColour'
  );

  const checkSavedBeforeClosing = () => {
    if (
      // To do: This doesn't always work - sometimes the confirmation is shown even if you haven't made any changes
      initialCalendarCellEvent?.hiresAssetRef !== customEventData.hiresAssetRef ||
      initialCalendarCellEvent?.eventText !== customEventData.eventText ||
      initialCalendarCellEvent?.textColour !== customEventData.textColour ||
      isDirty
    ) {
      // Show confirmation dialog
      ui.dispatch(
        UserInterfaceActions.setToolPalette({
          dirty: true,
          confirmationRequired: true,
          requestedPath: 'calendar-palette',
        })
      );
      ui.dispatch(UserInterfaceActions.setToolPaletteDirty(true));
      ui.dispatch(UserInterfaceActions.setToolPaletteSection('calendar-palette'));
    } else {
      // Close the cell editor
      ui.dispatch(
        UserInterfaceActions.setToolPalette({
          dirty: false,
          confirmationRequired: false,
          requestedPath: 'calendar-palette',
        })
      );
      ui.dispatch(UserInterfaceActions.setToolPaletteDirty(false));
      ui.dispatch(UserInterfaceActions.setToolPaletteSection('calendar-palette'));
      calendarManager.destroyPreview();
    }
  };

  const onCheckClose = () => {
    let requestedPath = 'calendar-palette';
    if (ui.state.palette.requestedPath !== '') {
      requestedPath = ui.state.palette.requestedPath;
    }
    ui.dispatch(
      UserInterfaceActions.setToolPalette({
        dirty: false,
        confirmationRequired: false,
        requestedPath: '',
      })
    );
    if (PALETTE_SECTIONS.includes(requestedPath)) {
      ui.dispatch(UserInterfaceActions.setToolPaletteSection(requestedPath));
    } else if (VIEW_MODES.includes(requestedPath)) {
      ui.dispatch(UserInterfaceActions.setViewMode(requestedPath as ViewMode));
    }
    ui.dispatch(
      UserInterfaceActions.setToolPalette({
        dirty: false,
        confirmationRequired: false,
        requestedPath: '',
      })
    );
    // handle closing palette if switch to preview is made
    if (
      ui.state.palette.requestedPath === 'preview' &&
      ui.state.palette.section === 'calendar-palette/cell-editor-palette' &&
      ui.state.palette.dirty === true
    ) {
      ui.dispatch(UserInterfaceActions.setToolPaletteSection('calendar-palette'));
    }
    calendarManager.destroyPreview();
  };

  useEffect(() => {
    if (
      initialCalendarCellEvent?.hiresAssetRef !== customEventData.hiresAssetRef ||
      initialCalendarCellEvent?.eventText !== customEventData.eventText ||
      initialCalendarCellEvent?.textColour !== customEventData.textColour
    ) {
      setIsDirty(true);
    }
  }, [customEventData, initialCalendarCellEvent]);

  useEffect(() => {
    if (customEventData?.textColour !== '' && ui.state.palette.section?.includes(sectionKey)) {
      ui.dispatch(
        UserInterfaceActions.setToolPaletteSection('calendar-palette/cell-editor-palette')
      );
    }
  }, [customEventData?.textColour]);

  // CALENDAR_COLOUR_RESET - if no event text, no picture and no predefined event text, reset the colour
  useEffect(() => {
    const token = app.core.subscribe(CoreEvent.CALENDAR_COLOUR_RESET, () => {
      setCustomEventData(current => {
        return {...current, textColour: ''};
      });
    });
    return () => {
      app.core.unsubscribe(token);
    };
  }, []);

  useEffect(() => {
    if (
      customEventData.eventText === '' &&
      customEventData.textColour !== '' &&
      customEventData.hiresAssetRef === '' &&
      !customEventData.hasEventSetEvent
    ) {
      setCustomEventData(current => {
        return {...current, textColour: ''};
      });
    }
    if (
      customEventData.hasEventSetEvent ||
      customEventData.eventText !== '' ||
      customEventData.hiresAssetRef !== ''
    ) {
      setShowColourPalette(true);
    } else {
      setShowColourPalette(false);
    }
  }, [customEventData.eventText, customEventData.hiresAssetRef]);

  // if an item is selcted when this palette is open de-select it
  useEffect(() => {
    if (ui.state.palette.section === `calendar-palette/${sectionKey}` && item?.ref !== -1) {
      app.core.dispatch('SHAPE.DESELECT', null).catch(error => {
        console.error(error);
      });
    }
  }, [ui.state.palette.section]);

  return (
    <ToolPaletteSection
      id={'calendarCellEditorPalette'}
      title={!loaded ? '' : (cellEvent?.dateTitle as string)}
      sectionKey={sectionKey}
      buttonClasses={sectionButtonClasses}
      subSection={true}
      showButton={false}
      subSectionPanelType={ui.state.isSmallScreen ? 'pop-up' : 'slide-in'}
      smallScreenPanelHeight={'full'}
      customCloseFunction={checkSavedBeforeClosing}
    >
      <div className={wrapperClasses}>
        {!loaded && (
          <LoadingOverlay open={true} usePortal={false} overlayColour={'themeBackgroundColour'} />
        )}
        <div className={'flex flex-col w-full space-y-sm overflow-auto'}>
          {allowCustomEvents && (
            <TextArea
              key={`text-area-${customEventData.ref}`}
              id={'cell-text-field'}
              name={'cell-text-field'}
              placeholder={t('calendars:str_LabelEnterSomeText')}
              className={'lg:min-h-[100rem] sm:min-h-[60rem] w-full'}
              style={{resize: 'none'}}
              debounceDelay={500}
              value={customEventData?.eventText}
              interacting={(typing: boolean) => {
                if (typing) {
                  setDisableSaveButton(true);
                } else {
                  setDisableSaveButton(false);
                }
              }}
              onChange={(e: any) => {
                setTextProcessing(true);
                setIsDirty(true);
                calendarManager.updateCell({eventText: e.target.value}).then(() => {
                  setTextProcessing(false);
                });
                setCustomEventData({
                  ...customEventData,
                  eventText: e.target.value,
                });
              }}
              rows={ui.state.isSmallScreen ? 2 : 4}
            />
          )}

          {canCustomiseDates && (showColourPalette || customEventData.hasEventSetEvent) && (
            <CalendarColourPalette
              selectedColour={customEventData?.textColour?.replace('#', '')}
              onClick={(event: any, newSelectedColour: Tile) => {
                calendarManager
                  .updateCell({
                    textColour: `#${newSelectedColour?.src}`,
                  })
                  .then(() => {
                    setIsDirty(true);
                  });
                setCustomEventData({
                  ...customEventData,
                  textColour: newSelectedColour?.src,
                });
              }}
              onRemoveColour={() => {
                calendarManager.updateCell({textColour: ''}).then(() => {
                  setIsDirty(true);
                });
                setCustomEventData({
                  ...customEventData,
                  textColour: '',
                });
              }}
            />
          )}

          {canCustomiseDates && (
            <CalendarPicturePalette
              sectionKey={'cell-picture-palette'}
              buttonLabel={t('*:str_LabelPicture')}
              assets={assets}
              server={server}
              selectedAssetId={customEventData.hiresAssetRef}
              onRemovePicture={() => {
                calendarManager.updateCell({hiresAssetRef: ''}).then(() => {
                  setIsDirty(true);
                });
                setCustomEventData({
                  ...customEventData,
                  hiresAssetRef: '',
                });
              }}
              onSelectPicture={(asset: any) => {
                if (customEventData.hiresAssetRef !== asset.id) {
                  calendarManager.updateCell({hiresAssetRef: asset.assetRef}).then(() => {
                    setIsDirty(true);
                  });
                  setCustomEventData({
                    ...customEventData,
                    hiresAssetRef: asset.assetRef,
                  });
                }
              }}
            />
          )}

          <div>
            <Heading size={3}>{t('*:str_TitlePreview')}</Heading>
            <div className={'relative'}>
              {textProcessing && (
                <LoadingOverlay open={true} usePortal={false} id={'cell-preview-loading-overlay'} />
              )}
              <Theme name={ThemeName.Container} className={previewContainerClasses}>
                <div className={previewWrapperClasses} id={'cell-preview'}>
                  <></>
                </div>
              </Theme>

              {customEventData?.hiresAssetRef !== '' && (
                <Button
                  id={'edit-picture-button'}
                  className={'absolute top-xs right-xs'}
                  label={t('*:str_ButtonEdit')}
                  size={'small'}
                  startIcon={<PanIcon />}
                  onClick={() => {
                    ui.dispatch(UserInterfaceActions.setCalendarEditPictureDialog(true));
                  }}
                />
              )}
            </div>
          </div>
        </div>
        {ui.state.palette.section === `calendar-palette/${sectionKey}` ? (
          <div id={'bottom-buttons'} className={bottomButtonsContainerClasses}>
            <Button
              label={t('*:str_LabelSaveAndClose')}
              className={'w-full max-w-[400rem]'}
              onClick={() => {
                calendarManager.updateCalendar().then(() => {
                  ui.dispatch(
                    UserInterfaceActions.setToolPalette({
                      dirty: false,
                      confirmationRequired: false,
                    })
                  );
                  ui.dispatch(UserInterfaceActions.setToolPaletteSection('calendar-palette'));
                });
              }}
              disabled={disableSaveButton}
            />
            <Button
              label={t('*:str_ButtonCancel')}
              className={'w-full max-w-[400rem]'}
              buttonStyle={'secondary'}
              onClick={checkSavedBeforeClosing}
            />
          </div>
        ) : (
          <></>
        )}
      </div>
      <CloseCellEditorDialog
        open={ui.state.palette.confirmationRequired}
        onDontSave={() => {
          setCustomEventData(current => {
            return {...current, eventText: cellEvent?.eventText as string};
          });
          // Close the confirmation dialog and the editor palettee
          calendarManager.cancelCellPreview().then(() => onCheckClose());
        }}
        onSave={() => {
          // Close the confirmation dialog, save the cell data and close the editor palette
          calendarManager.updateCalendar().then(() => onCheckClose());
        }}
      />
    </ToolPaletteSection>
  );
};

export default CellEditorPalette;
